import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ArrowRightIcon } from 'lucide-react';
import { NAV_LINKS, GET_STARTED_URL, SIGN_IN_URL, BOOK_DEMO_URL, VC_PATH } from '../../data/dgpt';
import { CtaButton } from '../ui/CtaButton';
import { DgptLogo } from './DgptLogo';
import { SolutionsMenu, MobileSolutionsAccordion } from './SolutionsMenu';
import { trackEvent } from '../../utils/analytics';
import { cn } from '../../src/lib/utils';
export function DgptHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);
  const isActive = (href: string) => !href.includes('#') && href !== '/' && pathname.startsWith(href);
  const solutionsActive = pathname.startsWith('/solutions') || pathname.startsWith(VC_PATH);
  return <header className={cn('fixed inset-x-0 top-0 z-40 w-full border-b transition-colors duration-300', scrolled ? 'border-hairline bg-ink/95 backdrop-blur-xl' : 'border-hairline-soft bg-ink/90 backdrop-blur-md')} data-id="element-87" data-mp="clk4qeuq8vky">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-lime focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ink" data-id="element-88" data-mp="mhww362suxcj">
        Skip to main content
      </a>

      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-4 px-4 sm:px-6 lg:h-[72px] lg:px-8" data-id="element-89" data-mp="22wew5orfhlcz">
        <Link to="/" aria-label="DiligenceGPT™ home" className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink" data-id="element-90" data-mp="2xbf5rg8t4v1">
          <DgptLogo data-id="element-91" data-mp="7u1prz9a3892" />
        </Link>

        <nav aria-label="Primary" className="hidden xl:block" data-id="element-92" data-mp="7v32e85y02sw">
          <ul className="flex items-center gap-5 2xl:gap-7" data-id="element-93" data-mp="3gb6ecq438dn1">
            {NAV_LINKS.map(link => {
            if (link.menu === 'solutions') return <SolutionsMenu key={link.label} active={solutionsActive} data-id="element-94" data-mp="3b8xinlztyxh8" />;
            const active = isActive(link.href);
            return <li key={link.label} data-id="element-95" data-mp="3kbghlv0hwpy7">
                  <Link to={link.href} aria-current={active ? 'page' : undefined} className={cn('relative whitespace-nowrap text-[13px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink 2xl:text-sm', active ? 'text-text-primary' : 'text-text-secondary')} data-id="element-96" data-mp="3kaf5robnnzff">
                    {link.label}
                    {active ? <span aria-hidden="true" className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-lime" data-id="element-97" data-mp="2dialffr1eoaj" /> : null}
                  </Link>
                </li>;
          })}
          </ul>
        </nav>

        <div className="flex items-center gap-3" data-id="element-98" data-mp="3643rby7mqj1j">
          <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('sign_in_click', {
          placement: 'header'
        })} className="hidden whitespace-nowrap text-[13px] font-medium text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 xl:inline 2xl:text-sm" data-id="element-99" data-mp="2r9p3nkd4elgj">
            Sign In
          </a>
          <CtaButton href={BOOK_DEMO_URL} variant="outline" event="enterprise_demo_click" eventParams={{
          placement: 'header'
        }} className="hidden whitespace-nowrap px-4 lg:inline-flex" data-id="element-100" data-mp="1w8q8ta3dy0lz">
            Book a Demo
          </CtaButton>
          <CtaButton href={GET_STARTED_URL} event="primary_cta_click" eventParams={{
          placement: 'header',
          cta: 'get_started'
        }} className="hidden whitespace-nowrap px-4 sm:inline-flex" data-id="element-101" data-mp="2jz3zoxa8htez">
            Get Started
            <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-102" data-mp="251lc3y9alm94" />
          </CtaButton>
          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="mobile-menu" className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-hairline text-text-secondary transition-colors hover:border-lime/50 hover:text-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime xl:hidden" data-id="element-103" data-mp="253xvmfup84oa">
            {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-104" data-mp="3f760c9of3bsb" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-105" data-mp="3jyo1axqfxcda" />}
            <span className="sr-only" data-id="element-106" data-mp="3jzz3it48mc2a">{open ? 'Close menu' : 'Open menu'}</span>
          </button>
        </div>
      </div>

      {open ? <div id="mobile-menu" className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-hairline bg-surface xl:hidden" data-id="element-107" data-mp="3sz2mlx3emrgs">
          <nav aria-label="Mobile" className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6" data-id="element-108" data-mp="1u2s28j85ck9">
            <ul className="grid sm:grid-cols-2 sm:gap-x-8" data-id="element-109" data-mp="6r318sbdevfl">
              {NAV_LINKS.map(link => {
            if (link.menu === 'solutions') {
              return <MobileSolutionsAccordion key={link.label} active={solutionsActive} onNavigate={() => setOpen(false)} data-id="element-110" data-mp="28jhgtkqfznsu" />;
            }
            const active = isActive(link.href);
            return <li key={link.label} data-id="element-111" data-mp="1whpw8r0h0dte">
                    <Link to={link.href} onClick={() => setOpen(false)} aria-current={active ? 'page' : undefined} className={cn('flex min-h-[48px] items-center border-b border-hairline-soft text-[15px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime', active ? 'text-lime' : 'text-text-secondary')} data-id="element-112" data-mp="1wisl6te8g66o">
                      {link.label}
                    </Link>
                  </li>;
          })}
              <li data-id="element-113" data-mp="33667ugsu3hee">
                <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('sign_in_click', {
              placement: 'mobile_menu'
            })} className="flex min-h-[48px] items-center border-b border-hairline-soft text-[15px] font-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime" data-id="element-114" data-mp="333k5cvksvs31">
                  Sign In
                </a>
              </li>
            </ul>
            <div className="my-4 grid gap-2 sm:grid-cols-2" data-id="element-115" data-mp="2y2tagyuhpefx">
              <CtaButton href={GET_STARTED_URL} event="primary_cta_click" eventParams={{
            placement: 'mobile_menu',
            cta: 'get_started'
          }} size="lg" className="w-full" onClick={() => setOpen(false)} data-id="element-116" data-mp="32vckxmxvrtgi">
                Get Started
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-117" data-mp="2f61pmkzaur9r" />
              </CtaButton>
              <CtaButton href={BOOK_DEMO_URL} variant="outline" event="enterprise_demo_click" eventParams={{
            placement: 'mobile_menu'
          }} size="lg" className="w-full" onClick={() => setOpen(false)} data-id="element-118" data-mp="2k437j2kzokjn">
                Book a Demo
              </CtaButton>
            </div>
          </nav>
        </div> : null}
    </header>;
}